// Operations > Brokers (was "Rentway"; owner, 23 Oct 2026): the booking sites' Dubai reservations read by the engine
// from Rentway and from the Comfy site (row.source), each checked against its Ejar booking (engine api/rentway.py).
// This file: what the page shows and the Excel rows, in the columns of the owner's own "Rentway Booking Log" sheet
// (+ the check's columns).

import { ejarCommentsOf } from '../../ContractsManagement/utils/ejarComments';

// the engine's check states, worst first: [label, Badge / status tone]
export const STATES = {
    cancel: ['Cancel on Ejar', 'danger'],
    book: ['Book on Ejar', 'danger'],
    fix: ['Check on Ejar', 'warning'],
    ok: ['On Ejar', 'success'],
    done: ['Contract / expired', 'neutral'],
    cancelled: ['Cancelled', 'neutral'],
    past: ['Past - not on Ejar', 'neutral'],
};
export const STATE_ORDER = ['cancel', 'book', 'fix', 'ok', 'done', 'cancelled', 'past'];
export const ACTION_STATES = new Set(['cancel', 'book', 'fix']);

/** A row's key on the page: a Comfy booking's number (its reference's, YEUAE-1003 -> 1003) can be a Rentway one's. */
export const rowKey = (r) => `${r.source || 'rentway'}-${r.number}`;

/** What the row's amount is: paid by the broker's voucher (Rentway) or to collect at the counter (Comfy). */
export const amountText = (r) => (r.amount_kind === 'counter' ? 'at the counter' : 'paid');

/** How often each site is read (Admin > Schedules; 0 = off): "Rentway every 4 h · Comfy every 1 h". */
export function intervalsText(status) {
    const every = status?.intervals || { rentway: status?.interval_h ?? 4 };
    return [['Rentway', every.rentway], ['Comfy', every.comfy]]
        .filter(([, h]) => h !== undefined && h !== null)
        .map(([site, h]) => (Number(h) > 0 ? `${site} every ${h} h` : `${site} read off`))
        .join(' · ');
}

export const money = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "2026-10-30T07:30:00" -> "30/10/2026 07:30" (Dubai wall time, as the engine sends it). */
export const dubaiText = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : '';
};

const CONTRACT_STATE_TEXT = { open: 'Open', cancelled: 'Cancelled', paused: 'Paused' };

/**
 * The Ejar contract a booking became (owner, 26 Oct 2026): when it was opened and when it was closed, as the contracts
 * store holds them - "Open" while it is open (its expected return is not a close). '' when there is no contract.
 */
export function contractTimes(ejar) {
    const e = ejar || {};
    if (!e.contract_no) return { opened: '', closed: '' };
    const closed = e.contract_closed ? dubaiText(e.contract_closed) : (CONTRACT_STATE_TEXT[e.contract_state] || '—');
    return { opened: dubaiText(e.contract_opened) || '—', closed };
}

/**
 * The Ejar comments of the contract a booking became (owner, 26 Oct 2026), newest first, as the engine last read them:
 * {list: [{key, day, time, by, text}], read: 'dd/mm/yyyy HH:MM' | '', notRead}. notRead: the comments were never read.
 */
export function contractComments(ejar) {
    const e = ejar || {};
    if (!e.contract_no) return { list: [], read: '', notRead: false };
    const notRead = !Array.isArray(e.contract_comments);
    return { list: ejarCommentsOf({ ejar_comments: e.contract_comments }), read: dubaiText(e.contract_comments_read_at), notRead };
}

/** The comments as one text (the Excel cell): "dd/mm/yyyy HH:MM · by: text", one per line. */
export const commentsText = (ejar) => contractComments(ejar).list
    .map((c) => `${c.day} ${c.time}${c.by ? ` · ${c.by}` : ''}: ${c.text}`).join('\n');

export function counts(rows) {
    const out = { all: rows.length, action: 0 };
    STATE_ORDER.forEach((s) => { out[s] = 0; });
    rows.forEach((r) => {
        out[r.state] = (out[r.state] || 0) + 1;
        if (ACTION_STATES.has(r.state)) out.action += 1;
    });
    return out;
}

/**
 * What Yelo did on Ejar for a reservation (phase 2: "Book on Ejar"), newest action of each kind: [[text, tone], ...].
 * Tones: 'success' | 'danger'.
 */
export function yeloLines(r) {
    const y = r.yelo || {};
    const out = [];
    const b = y.book;
    if (b) {
        const when = dubaiText(b.at);
        if (b.outcome === 'done') out.push([`Booked by Yelo ${b.booking_number || ''} · ${when}`, 'success']);
        else if (b.outcome === 'price') out.push([`Not booked - Ejar's price AED ${money(b.amount)} differs · ${when}`, 'danger']);
        else out.push([`Not booked: ${b.message || b.outcome} · ${when}`, 'danger']);
    }
    const mailLine = (e, what) => {
        const test = e.detail?.test ? ` (test to ${(e.detail.to || []).join(', ')})` : '';
        return e.outcome === 'done'
            ? [`${what} sent${test} · ${dubaiText(e.at)}`, 'success']
            : [`${what} not sent: ${e.message || ''}`, 'danger'];
    };
    if (y.email) out.push(mailLine(y.email, 'E-mail'));
    const c = y.cancel;
    if (c) {
        const when = dubaiText(c.at);
        out.push(c.outcome === 'done'
            ? [`Cancelled on Ejar by Yelo ${c.booking_number || ''} · ${when}`, 'success']
            : [`Not cancelled: ${c.message || c.outcome} · ${when}`, 'danger']);
    }
    if (y.email_cancel) out.push(mailLine(y.email_cancel, 'Cancellation e-mail'));
    const a = y.auto;          // what the automatic pass could not do - unless a book / cancel came after it
    if (a && !((b && b.id > a.id) || (c && c.id > a.id))) {
        out.push([`Not done automatically: ${a.message || ''} · ${dubaiText(a.at)}`, 'danger']);
    }
    const u = y.customer;      // Ejar refused the new customer (owner, 3 Nov 2026: 996's phone) - while still to book
    if (u && u.outcome === 'failed' && r.state === 'book' && !(b && b.outcome === 'done' && b.id > u.id)) {
        out.push([`Not booked: ${customerRefusal(u)} · ${dubaiText(u.at)}`, 'danger']);
    }
    const m = y.reminder;      // the pick-up reminder on Telegram, while it is still to book
    if (m && r.state === 'book') {
        out.push([`Told on Telegram: not on Ejar, ${m.detail?.hours || '?'} h before pick-up · ${dubaiText(m.at)}`, 'danger']);
    }
    return out;
}

/** Ejar's answer to the new customer in plain words (the engine's rentway_agent.plain_reason, short). */
export function customerRefusal(u) {
    const msg = String(u?.message || '');
    if (/invalid phone number/i.test(msg)) {
        return `Ejar refused the phone ${u.detail?.phone || ''} (not valid for its country) - type the right phone in Book on Ejar`;
    }
    if (/HTTP 400/.test(msg) && /not valid|validation/i.test(msg)) return 'Ejar refused the new customer';
    return 'Ejar answered an error to the new customer - check Ejar';
}

/** The last automatic pass (the read's result) in a few words, '' when it did not run. */
export function autoSummary(agent) {
    if (!agent || agent.off) return '';
    if (agent.error) return `Automatic pass: ${agent.error}`;
    const n = (k) => (agent[k] || []).length;
    const parts = [`booked ${n('booked')}`, `cancelled ${n('cancelled')}`];
    if (n('needs_you')) parts.push(`${n('needs_you')} need you`);
    if (n('errors')) parts.push(`${n('errors')} tried again next time`);
    if (agent.left) parts.push(`${agent.left} wait for the next pass`);
    return `Last automatic pass: ${parts.join(' · ')}`;
}

/** A booking Yelo cancelled on Ejar whose cancellation e-mail has not reached the branches (none, failed, a test). */
export function needsCancelEmail(r) {
    const y = r.yelo || {};
    if (y.cancel?.outcome !== 'done') return false;
    return !y.email_cancel || y.email_cancel.outcome !== 'done' || !!y.email_cancel.detail?.test;
}

/**
 * A booking Yelo made whose branch e-mail has not reached the branches (none, failed, or only a test one) - also a
 * Save Ejar answered with an error that went through after all (Ejar's own read holds it, still open).
 */
export function needsBranchEmail(r) {
    const y = r.yelo || {};
    const ejar = r.ejar || {};
    const made = y.book?.outcome === 'done'
        || (y.book?.outcome === 'failed' && ejar.source !== 'yelo' && ejar.status_id === 1000 && !!ejar.booking_number);
    if (!made) return false;
    return !y.email || y.email.outcome !== 'done' || !!y.email.detail?.test;
}

const haystack = (r) => [r.number, r.reference, r.customer, r.phone, r.broker, r.broker_name, r.group, r.car,
    r.ejar?.booking_number, r.ejar?.contract_no, r.flight, r.site].filter(Boolean).join(' ').toLowerCase();

/** The rows of one state tile ('all' / 'action' / a state) matching the search words. */
export function visibleRows(rows, filter, search) {
    const words = String(search || '').toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
        if (filter === 'action' ? !ACTION_STATES.has(r.state) : filter !== 'all' && r.state !== filter) return false;
        if (!words.length) return true;
        const text = haystack(r);
        return words.every((w) => text.includes(w));
    });
}

// ExcelJS writes a date as its UTC day: midnight UTC keeps the day the engine sent (a local midnight in Dubai is the
// day before in UTC - every date came out one day early, review 23 Oct 2026)
const datePart = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
};
const timePart = (iso) => (/T(\d{2}:\d{2})/.exec(String(iso || '')) || [])[1] || '';

// the owner's sheet's columns first (his spelling of "Referance" and "Rentey" kept), then the check's
export const EXCEL_COLUMNS = [
    ['Booking Date', 14], ['Reservation No', 14], ['Broker Name / Code', 24], ['Referance', 20], ['Phone No', 18],
    ['Status', 22], ['Branch', 9], ['Date Out', 12], ['Time', 8], ['Date In', 12], ['Time', 8], ['Amount Paid', 13],
    ['Add on', 11], ['Customer Name', 28], ['Rentey Booking No', 19], ['Rentey Contract No', 21], ['Note', 50],
    ['Car Group', 10], ['Car', 18], ['Days', 7], ['Ejar Status', 14], ['Ejar Amount', 13], ['Check', 18],
    ['Add on items', 28], ['Ejar comment', 70], ['Site', 10], ['Amount Is', 15], ['Contract Opened', 17],
    ['Contract Closed', 17], ['Contract Comments', 70],
];

/** One reservation as the Excel row (EXCEL_COLUMNS' order). */
export function excelRow(r) {
    const ejar = r.ejar || {};
    const [stateLabel] = STATES[r.state] || [r.state];
    const contract = contractTimes(ejar);
    return [
        datePart(r.created_at), r.number,
        r.client_id ? `${r.client_id} - ${r.broker_name || r.broker || ''}` : (r.broker_name || r.broker || ''), r.reference || '',
        r.phone || '', r.status_name || r.status || '', r.station || '', datePart(r.date_out), timePart(r.date_out),
        datePart(r.date_in), timePart(r.date_in), Number(r.invoice) || 0, r.cashier ? Number(r.cashier) : null,
        r.customer || '', ejar.booking_number || '', ejar.contract_no || '',
        [...(r.reasons || []), ...yeloLines(r).map(([text]) => text)].join('; '),
        r.group || '', r.car || '', r.days ?? '', ejar.status || '', ejar.amount ?? null, stateLabel,
        r.cashier_items || '', r.comment || '', r.site || 'Rentway', amountText(r), contract.opened, contract.closed,
        commentsText(ejar),
    ];
}
